import { PlusIcon } from '@heroicons/react/24/outline';
import { TrashIcon } from '@heroicons/react/24/solid';
import React from 'react';
import type { FC } from 'react';

import { useDeleteFieldMutation } from '@/api/queries';
import type { Field } from '@/api/types';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { DocumentIcon } from '@/common/document-icon';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { PictureIcon } from '@/common/picture-icon';
import { TextIcon } from '@/common/text-icon';

import { Menu } from '@/common/menu';
import { useTemplateEditorStore } from '../store';
import { FieldsPanelView } from '../types';
import { FieldCard } from './field-card';
import { FieldCardLinkedLayers } from './field-card-linked-layers';

export const FieldsListView: FC = () => {
    const fields = useTemplateEditorStore((s) => s.template?.latestVersion.fields);
    const width = useTemplateEditorStore((s) => s.fieldsPanelWidth);
    const setFields = useTemplateEditorStore((s) => s.setFields);
    const setFieldsPanelView = useTemplateEditorStore((s) => s.setFieldsPanelView);
    const setEditedFieldId = useTemplateEditorStore((s) => s.setEditedFieldId);
    const setTemplateEdited = useTemplateEditorStore((s) => s.setIsEdited);

    const { mutate: deleteField } = useDeleteFieldMutation({
        onSuccess: (_: unknown, id: number) => {
            const newFields = fields?.filter((f) => f.id !== id) || [];
            setFields(newFields);
            setTemplateEdited();
        },
    });

    const onCreateTextField = () => setFieldsPanelView(FieldsPanelView.TextFieldEdit);
    const onCreateImageField = () => setFieldsPanelView(FieldsPanelView.ImageFieldEdit);

    const onEditField = (field: Field) => {
        setEditedFieldId(field.id as number);
        setFieldsPanelView(FieldsPanelView.TextFieldEdit);
    };

    const onDeleteField = (field: Field) => {
        if (window.confirm(`Are you sure you want to delete the field "${field.name}"?`)) {
            deleteField(field.id as number);
        }
    };

    return (
        <FloatingPanel
            className="pr-4 py-4 h-full flex-shrink-0 flex-grow-0 overflow-y-auto"
            type={FloatingPanelType.LIGHT}
            width={width}
        >
            <FloatingPanelItem className="flex flex-row justify-between items-center px-4">
                <div className="flex flex-row gap-2 items-center">
                    <DocumentIcon className="w-5 h-5 text-white" />
                    <p className="text-base text-white font-medium">Content Fields</p>
                </div>
                <Menu
                    icon={<PlusIcon className="w-5 h-5 text-white hover:text-gray-30 cursor-pointer" />}
                    items={[
                        {
                            icon: <TextIcon />,
                            name: 'Text Field',
                            onClick: onCreateTextField,
                            id: 'Text Field',
                        },
                        {
                            icon: <PictureIcon />,
                            name: 'Image Field',
                            onClick: onCreateImageField,
                            id: 'Image Field',
                        },
                    ]}
                />
            </FloatingPanelItem>

            <FloatingPanelItem className="h-full px-4">
                {fields && fields.length > 0 ? (
                    <div className="flex flex-col gap-2 overflow-y-scroll">
                        {fields.map((field) => (
                            <FieldCard
                                key={field.id}
                                field={field}
                                linkedLayers={<FieldCardLinkedLayers linkedLayers={field.linkedLayers} />}
                                onClick={() => onEditField(field)}
                                quickAction={
                                    <TrashIcon
                                        className="size-4 mt-1 stroke-red-50 cursor-pointer"
                                        onClick={(e) => {
                                            onDeleteField(field);
                                            e.stopPropagation();
                                        }}
                                    />
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center h-full">
                        <p className="text-sm text-gray-50 ">Start creating your first</p>
                        <div className="flex flex-row gap-2 items-center mt-2">
                            <Button
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                leading={<TextIcon />}
                                title="Text Field"
                                onClick={onCreateTextField}
                            />
                            <p className="text-gray-100 text-xs">or</p>
                            <Button
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                leading={<PictureIcon />}
                                title="Image Field"
                                onClick={onCreateImageField}
                            />
                        </div>
                    </div>
                )}
            </FloatingPanelItem>
        </FloatingPanel>
    );
};
